import { BonusThresholdMetric } from '@/types/api/bonus';
import { Sale } from '@/types/api/sale';
import { bonusAmountText, bonusValueText, formatKronor, thresholdText } from './bonus';
import { PROFIT_RATE_STEPS } from './profit';

export interface SaleStep {
  /** Item count where the step is reached. */
  itemCount: number;
  /** "45 % vinst" or "+5 % extra vinst". */
  label: string;
  kind: 'tier' | 'bonus';
  reached: boolean;
}

/**
 * Item counts where the sale's profit goes up: higher profit tiers and item-based bonus thresholds.
 * One step per item count, so a tier and bonuses at the same count share a tick and a label.
 */
export function getSaleSteps(sale: Pick<Sale, 'unitCount' | 'bonuses'>): SaleStep[] {
  const groups = new Map<number, { tier?: string; bonuses: string[]; reached: boolean }>();
  const group = (itemCount: number) => {
    if (!groups.has(itemCount)) groups.set(itemCount, { bonuses: [], reached: true });
    return groups.get(itemCount)!;
  };

  for (const tier of PROFIT_RATE_STEPS.filter((step) => step.itemCount > 0)) {
    const g = group(tier.itemCount);
    g.tier = `${tier.profitRate / 100} % vinst`;
    g.reached &&= sale.unitCount >= tier.itemCount;
  }

  for (const bonus of sale.bonuses) {
    if (bonus.revokedAt || bonus.threshold?.metric !== BonusThresholdMetric.ItemCount) continue;
    const g = group(bonus.threshold.value);
    g.bonuses.push(bonusAmountText(bonus));
    g.reached &&= bonus.applied;
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([itemCount, g]) => {
      const bonus = g.bonuses.length ? `${joinWithOch(g.bonuses)} extra vinst` : null;

      return {
        itemCount,
        // "45 % vinst", "+3 % och 300 kr extra vinst" or "45 % vinst, +3 % extra vinst"
        label: [g.tier, bonus].filter(Boolean).join(', '),
        kind: bonus ? 'bonus' : 'tier',
        reached: g.reached,
      };
    });
}

const joinWithOch = (parts: string[]) =>
  parts.length > 1 ? `${parts.slice(0, -1).join(', ')} och ${parts[parts.length - 1]}` : parts[0];

/**
 * The next thing that raises the profit, e.g. "45 % vinst vid 100 st · 50 kvar". Item steps come
 * first since they're on the meter; revenue-based bonuses only once those are all reached.
 */
export function getNextStepText(sale: Pick<Sale, 'unitCount' | 'revenue' | 'bonuses'>): string | null {
  const next = getSaleSteps(sale).find((step) => !step.reached);
  if (next) {
    return `${next.label} vid ${next.itemCount.toLocaleString('sv-SE')} st · ${(next.itemCount - sale.unitCount).toLocaleString('sv-SE')} kvar`;
  }

  const revenueBonus = sale.bonuses
    .filter((b) => !b.revokedAt && !b.applied && b.threshold?.metric === BonusThresholdMetric.Revenue)
    .sort((a, b) => a.threshold!.value - b.threshold!.value)[0];
  if (revenueBonus) {
    const left = revenueBonus.threshold!.value - sale.revenue.amount;
    return `${bonusValueText(revenueBonus)} ${thresholdText(revenueBonus.threshold!)} · ${formatKronor(left, sale.revenue.currency)} kvar`;
  }

  return null;
}
